import { cloneElement, createContext, isValidElement, useContext, useState, type ReactElement, type ReactNode, type HTMLAttributes } from "react";
import { autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useMergeRefs, useRole, FloatingPortal, FloatingFocusManager, safePolygon, type Placement, size as sizeMw } from "@floating-ui/react";
import { Icon } from "./icons.js";

/**
 * Where floating UI is portalled. Provided by each designer instance so several
 * editors on one page keep their own — and correctly themed — portal target.
 */
export const PortalContext = createContext<HTMLElement | null>(null);

function useRootEl(): HTMLElement | null {
  const fromContext = useContext(PortalContext);
  const [fallback] = useState<HTMLElement | null>(() => (typeof document !== "undefined" ? document.body : null));
  return fromContext ?? fallback;
}

/**
 * React 19 made `ref` an ordinary prop, which quietly broke anchoring here.
 * Spreading the child's props over the floating reference — `{ ref: setReference,
 * ...children.props }` — now overwrites that ref with the child's own, so a
 * child that sets `ref` at all (every `Button`, because `forwardRef` passes one
 * through even when the caller gave none) left the tooltip with no anchor and it
 * never opened. Setting the ref last and merging the two keeps both.
 */
function useReferenceRef(children: ReactElement, setReference: (node: HTMLElement | null) => void) {
  const own = (children as ReactElement & { props?: { ref?: unknown } }).props?.ref;
  return useMergeRefs([setReference, (own ?? null) as never]);
}

export function Tooltip({ content, shortcut, children, placement = "bottom" }: { content: ReactNode; shortcut?: string; children: ReactElement; placement?: Placement }) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context, isPositioned } = useFloating({ open, onOpenChange: setOpen, placement, middleware: [offset(6), flip(), shift({ padding: 6 })], whileElementsMounted: autoUpdate });
  const hover = useHover(context, { delay: { open: 450, close: 60 }, move: false });
  const focus = useFocus(context);
  const role = useRole(context, { role: "tooltip" });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role, dismiss]);
  const root = useRootEl();
  const referenceRef = useReferenceRef(children, refs.setReference);
  if (!isValidElement(children)) return children;
  return (
    <>
      {cloneElement(children as ReactElement<HTMLAttributes<HTMLElement>>, getReferenceProps({ ...(children.props as HTMLAttributes<HTMLElement>), ref: referenceRef }))}
      {open ? (
        <FloatingPortal root={root}>
          <div ref={refs.setFloating} style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden" }} className={`bpe-scope bpe-tooltip ${isPositioned ? "bpe-floating--in" : ""}`} {...getFloatingProps()}>
            {content}
            {shortcut ? <kbd className="bpe-kbd">{shortcut}</kbd> : null}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

export interface PopoverProps {
  trigger: ReactElement;
  children: ReactNode | ((close: () => void) => ReactNode);
  placement?: Placement;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Match the trigger width. */
  matchWidth?: boolean;
  hoverable?: boolean;
  initialFocus?: number | false;
}

export function Popover({ trigger, children, placement = "bottom-start", className, open: controlledOpen, onOpenChange, matchWidth, hoverable, initialFocus = 0 }: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  const setOpen = (v: boolean) => {
    setUncontrolled(v);
    onOpenChange?.(v);
  };
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 }), sizeMw({ apply({ rects, elements, availableHeight }) { Object.assign(elements.floating.style, { maxHeight: `${Math.max(160, availableHeight - 8)}px`, ...(matchWidth ? { width: `${rects.reference.width}px` } : {}) }); } })],
    whileElementsMounted: autoUpdate
  });
  const click = useClick(context);
  const hover = useHover(context, { enabled: !!hoverable, handleClose: safePolygon() });
  const dismiss = useDismiss(context, { outsidePress: (e) => !(e.target as HTMLElement | null)?.closest?.(".bpe-popover") });
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss, role]);
  const root = useRootEl();
  const referenceRef = useReferenceRef(trigger, refs.setReference);
  return (
    <>
      {cloneElement(trigger as ReactElement<HTMLAttributes<HTMLElement>>, getReferenceProps({ ...(trigger.props as HTMLAttributes<HTMLElement>), ref: referenceRef }))}
      {open ? (
        <FloatingPortal root={root}>
          <FloatingFocusManager context={context} modal={false} initialFocus={initialFocus === false ? -1 : initialFocus} returnFocus>
            <div ref={refs.setFloating} style={{ ...floatingStyles, overflow: "auto", visibility: isPositioned ? "visible" : "hidden" }} className={`bpe-scope bpe-popover ${isPositioned ? "bpe-floating--in" : ""} ${className ?? ""}`} {...getFloatingProps()}>
              {typeof children === "function" ? children(() => setOpen(false)) : children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
}

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: string;
  /** Short, right-aligned: a keyboard shortcut or a count. */
  hint?: string;
  /** Longer prose, rendered on a second line under the label. */
  description?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  separatorBefore?: boolean;
}

export function Menu({ trigger, items, placement, label }: { trigger: ReactElement; items: MenuItem[]; placement?: Placement; label?: string }) {
  return (
    <Popover trigger={trigger} placement={placement} className="bpe-menu">
      {(close) => (
        <div role="menu" aria-label={label}>
          {items.map((it) => (
            <div key={it.id}>
              {it.separatorBefore ? <div className="bpe-menu__sep" /> : null}
              <button
                type="button"
                role="menuitem"
                className={`bpe-menu__item ${it.description ? "bpe-menu__item--stacked" : ""} ${it.danger ? "bpe-menu__item--danger" : ""}`}
                disabled={it.disabled}
                title={it.description}
                onClick={() => {
                  close();
                  it.onSelect?.();
                }}
              >
                {it.icon ? <Icon name={it.icon} size={14} /> : null}
                <span className="bpe-menu__body">
                  <span className="bpe-menu__label-text">{it.label}</span>
                  {it.description ? <span className="bpe-menu__desc">{it.description}</span> : null}
                </span>
                {it.hint ? <span className="bpe-menu__hint">{it.hint}</span> : null}
              </button>
            </div>
          ))}
        </div>
      )}
    </Popover>
  );
}
